import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy,
  getDoc,
  doc,
} from 'firebase/firestore';
import { format } from 'date-fns';
import { useSettings } from '~/hooks/settings';
import { DB, Realtime } from '~/services/firebase';

import App from '~/layouts/App';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import IOrder from '~/interfaces/IOrder';
import { useAppAuth } from '~/hooks/appAuth';
import { ORDER_STATUS } from '~/utils/getEnums';
import OrderDetails from './OrderDetails';
import IClient from '~/interfaces/IClient';

const Orders: React.FC = () => {
  const { user } = useAppAuth();
  const { company } = useSettings();
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState({} as IOrder);

  const [client, setClient] = useState<null | IClient>(null);

  const loadOrders = useCallback(async () => {
    const q = query(
      collection(DB, 'orders'),
      where('whatsapp', '==', user.whatsapp),
      // where('company_id', '==', company.uid),
      orderBy('created_at', 'desc'),
      limit(5),
    );
    const querySnapshot = await getDocs(q);

    const items: IOrder[] = [];

    querySnapshot.docs.forEach(item => {
      const itemOrder = item.data() as IOrder;
      const created_at = new Date(itemOrder.created_at.seconds * 1000);

      const parseItem = {
        ...itemOrder,
        uid: item.id,
        total_formatted: getCurrencyFormatted(itemOrder.total),
        created_at_formatted: format(created_at, "dd/MM/yyyy 'às' HH:mm"),
      } as IOrder;

      items.push(parseItem);
    });

    setOrders(items);
  }, [user, company]);

  useEffect(() => {
    if (user && user.uid && company.uid) loadOrders();
  }, [loadOrders, company, user]);

  const loadNotifications = useCallback(() => {
    if (user && user.uid) {
      const notificationsRef = Realtime.ref(`orders/${user.uid}`);
      const startKey = notificationsRef.push().key;

      notificationsRef
        .orderByKey()
        .startAt(startKey)
        .on('child_added', data => {
          const value = {
            ...data.val(),
            uid: data.key,
          };

          const items = [...orders];
          const findIndex = items.findIndex(
            find => find.uid === value.order_id,
          );

          if (findIndex !== -1) {
            items[findIndex].status = value.status;

            setOrders(items);
          }
        });
    }
  }, [user, orders]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    async function loadClient() {
      const docClient = await getDoc(doc(DB, 'clients', user.uid));
      const dataClient = docClient.data() as IClient;
      setClient(dataClient);
    }

    loadClient();
  }, [user.uid]);

  return (
    <App loading={false}>
      {selectedOrder && selectedOrder.uid && (
        <OrderDetails
          order={selectedOrder}
          onClose={() => setSelectedOrder({} as IOrder)}
        />
      )}
      {orders.length === 0 && (
        <div className="row mb-3 mt-5">
          <div className="col align-self-center text-center">
            <h5 className="mb-0">Você ainda não realizou nenhum pedido.</h5>
            <div className="row mb-3 mt-4">
              <div className="col align-self-center d-grid centered">
                <Link
                  to={`/${company.app_domain}`}
                  className="btn btn-danger btn-lg shadow-sm text-white"
                  style={{
                    background: company.app_color,
                    width: 200,
                    border: 0,
                  }}
                >
                  Ir às compras
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {company.fidelit && company.fidelit_quantity && company.fidelit_value && (
        <div className="row mb-2 mt-4">
          <div className="col align-self-center">
            <h5 className="mb-0">Fidelidade</h5>
          </div>

          <div className="d-flex flex-wrap">
            {Array(company.fidelit_quantity)
              .fill(0)
              .map((item, index) => (
                <div style={{ marginRight: 6 }}>
                  <i
                    key={index}
                    className={`bi bi-${
                      index + 1 <= (client?.fidelit_order_quantity || 0)
                        ? 'star-fill'
                        : 'star'
                    }`}
                    style={{ fontSize: 28, color: '#fcd34d' }}
                  />
                </div>
              ))}
          </div>

          {(client?.fidelit_order_quantity || 0) >=
          (company.fidelit_quantity || 0) ? (
            <p>
              {`Parabéns!!! Você completou todos os pedidos fidelidade e ganhou um
          desconto no valor de ${getCurrencyFormatted(
            client?.fidelit_balance || 0,
          )} no seu próximo pedido.`}
            </p>
          ) : (
            <p>
              {`Complete todos os pedidos fidelidade e ganhe um desconto no valor de ${getCurrencyFormatted(
                company.fidelit_quantity * company.fidelit_value,
              )} no seu próximo pedido.`}
            </p>
          )}
        </div>
      )}

      {orders.length > 0 && (
        <div className="row mb-2 mt-4">
          <div className="col align-self-center">
            <h5 className="mb-0">Meus pedidos</h5>
          </div>
          {orders.map(item => (
            <div className="col-12 col-md-6 col-lg-4" key={item.uid}>
              <div
                className="card shadow-sm product mb-3"
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedOrder(item)}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col align-self-center">
                      <p className="mb-0">
                        <small className="text-opac">
                          {item.created_at_formatted}
                        </small>
                      </p>
                      <h6
                        className="text-color-theme"
                        style={{ color: company.app_color }}
                      >
                        {`PEDIDO #${item.code}`}
                      </h6>
                      <div className="row">
                        <div className="col">
                          <p className={`status status-${item.status}`}>
                            <span>{ORDER_STATUS[item.status]}</span>
                          </p>
                        </div>
                        <div className="col-auto">
                          <span
                            className="link text-color-theme px-0"
                            style={{
                              color: company.app_color,
                              fontWeight: 'bold',
                            }}
                          >
                            {item.total_formatted}
                            <i className="bi bi-chevron-right" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </App>
  );
};

export default Orders;
